import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { blogFetch } from '@/store/slices/blog';
import { PageShell } from '../../components/PageShell';
import { Panel } from '../../components/Panel';
import { BackHeader } from '../../components/BackHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { ROUTE } from '../../routes';
import { removeNewArticle } from '../blog/newArticles';
import { ArticleView } from './components/ArticleView';
import { Section, RetryWrap } from './blog-preview.styles';

/**
 * The article preview route (`/blog/:id/preview`): resolves the saved article
 * from the blog slice and hands it to `ArticleView`, which owns the replicated
 * whitelabels article anatomy (and is reused by the editor's preview sheet).
 */
export const PageBlogPreview = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const loaded = useAppSelector((state) => state.blog.loaded);
  const loading = useAppSelector((state) => state.blog.loading);
  const fetchError = useAppSelector((state) => state.blog.error);
  const articles = useAppSelector((state) => state.blog.articles);
  const categories = useAppSelector((state) => state.blog.categories);

  React.useEffect(() => {
    // The error guard stops the retry loop a rejected fetch would otherwise
    // cause (loading flips back to false with loaded still false).
    if (!loaded && !loading && fetchError == null) void dispatch(blogFetch());
  }, [dispatch, loaded, loading, fetchError]);

  // Opening the article is what makes it "seen": its "New" badge on the list
  // (the Autopilot notice the owner followed from Home) goes with it. Keyed on
  // the route param and the brand the list wrote it under.
  const brandId = useAppSelector((state) => state.casinoInstance.activeBrand?.id);
  React.useEffect(() => {
    if (id && brandId) removeNewArticle(brandId, id);
  }, [id, brandId]);

  const article = articles.find((a) => a.id === id);

  const resolveHref = React.useCallback(
    (slug: string): string | undefined => {
      const target = articles.find((a) => a.slug === slug);
      return target ? ROUTE.blogPreview(target.id) : undefined;
    },
    [articles],
  );

  if (!article) {
    return (
      <PageShell>
        <Section>
          <BackHeader title={t('Article preview')} onBack={() => navigate(ROUTE.blog())} />
          {fetchError != null ? (
            <Panel>
              <EmptyState text="The blog could not be loaded." />
              <RetryWrap>
                <Button label={t('Retry')} variant="outline" onClick={() => void dispatch(blogFetch())} />
              </RetryWrap>
            </Panel>
          ) : loaded ? (
            <Panel>
              <EmptyState text="This article does not exist anymore." />
            </Panel>
          ) : (
            <Skeleton height={420} block />
          )}
        </Section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Section>
        <BackHeader
          title={t('Article preview')}
          onBack={() => navigate(ROUTE.blog())}
          action={<Button label={t('Edit')} icon="edit" onClick={() => navigate(ROUTE.blogEdit(article.id))} />}
        />
        <ArticleView
          article={article}
          articles={articles}
          categories={categories}
          onOpenArticle={(articleId) => navigate(ROUTE.blogPreview(articleId))}
          resolveHref={resolveHref}
        />
      </Section>
    </PageShell>
  );
};

export default PageBlogPreview;
